import React, { useState, useEffect } from "react";
import axios from "axios";
import {
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Autocomplete,
    TextField,
    Typography,
    Chip,
} from "@mui/material";
import { Delete as DeleteIcon, Edit as EditIcon, Add as AddIcon, GroupWork as GroupIcon, Refresh as RefreshIcon } from "@mui/icons-material";
import configs from "../Constants";
import ProductOffers from "./sub_comp/ProductOffers";
import { toast } from "react-toastify";
import DeleteDiaologue from "./sub_comp/Delete";
import PrintIcon from "@mui/icons-material/Print";

const Offers = () => {
    const [offers, setOffers] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openProductSelect, setOpenProductSelect] = useState(false);
    const [openGroupSelect, setOpenGroupSelect] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [showOfferDialog, setShowOfferDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [deleteItemName, setDeleteItemName] = useState("");

    const baseURL = configs.baseURL;
    const merchantData = localStorage.getItem("merchantData")
        ? JSON.parse(localStorage.getItem("merchantData"))
        : null;
    const merchCode = merchantData ? merchantData.merchantCode : "";
    const userToken = localStorage.getItem("token") || "";

    useEffect(() => {
        fetchProducts();
    }, []);

    const fetchOffers = async () => {
        setLoading(true);
        try {
            const response = await axios.get(
                `${baseURL}/api/offers?merchantCode=${merchCode}` 
            );
            setOffers(response.data);
        } catch (error) {
            console.error("Error fetching offers:", error);
            // toast.error("Failed to fetch offers.");
        } finally {
            setLoading(false);
        }
    };

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const response = await axios.get(
                `${baseURL}/api/products?merchantCode=${merchCode}`
            );
            setProducts(response.data);
        } catch (error) {
            console.error("Error fetching products:", error);
        } finally {
            fetchOffers();
        }
    };

    const handleDelete = (offerId, typeDescription) => {
        setDeleteItemId(offerId);
        setDeleteItemName(`Offer: ${typeDescription}`);
        setOpenDeleteDialog(true);
    };

    const handleDeleteClose = () => {
        setOpenDeleteDialog(false);
        setDeleteItemId(null);
    };

    const sendPrintOffer = (offer) => {
        let offerTxt = offer.type === "BUY X GET Y FREE" ? `Buy ${offer.X} Get ${offer.Y} Free` :
            offer.type === "BUY X GET % OFF" ? `Buy ${offer.X} Get ${offer.Y}% Off` :
                offer.type === "Flat % OFF" ? `${offer.Y}% Off` :
                    offer.type === "BUY X FOR FLAT Y" ? `Buy ${offer.X} For Flat ${offer.Y}` : ""
        let printDetails = { printType: "OFFER" };
        printDetails.offerTxt = offerTxt;
        let isAndroid = false;
        if (window.PrintInterface) {
            isAndroid = true;
            window.PrintInterface.print(JSON.stringify(printDetails));

        }
        if (!isAndroid && window?.chrome?.webview?.postMessage) {
            window.chrome.webview.postMessage(printDetails);
        }

    };

    const handleConfirmDelete = async () => {
        if (!deleteItemId) return;
        try {
            await axios.delete(
                `${baseURL}/api/offers/${deleteItemId}?merchantCode=${merchCode}`,
                { headers: { Authorization: `Bearer ${userToken}` } }
            );
            toast.success("Offer deleted successfully.");
            fetchOffers();
        } catch (error) {
            console.error("Error deleting offer:", error);
            toast.error("Failed to delete offer.");
        } finally {
            handleDeleteClose();
        }
    };

    const handleAddOfferClick = () => {
        setSelectedProduct(null);
        setSelectedProducts([]);
        setOpenProductSelect(true);
    };

    const handleAddGroupOfferClick = () => {
        setSelectedProduct(null);
        setSelectedProducts([]);
        setOpenGroupSelect(true);
    };

    const handleProductSelect = (event, newValue) => {
        if (newValue) {
            setSelectedProduct(newValue);
            setOpenProductSelect(false);
            setShowOfferDialog(true);
        }
    };

    const handleGroupSelect = () => {
        if (selectedProducts.length > 0) {
            setOpenGroupSelect(false);
            setShowOfferDialog(true);
        } else {
            toast.error("Please select at least one product for the group.");
        }
    };

    const handleEditClick = (offer) => {
        // Determine product from offer (assuming offer has productId or we find it)
        if (offer.productIds && offer.productIds.length > 0) {
            // Group offer
            const groupProducts = products.filter(p => offer.productIds.includes(p.id));
            setSelectedProducts(groupProducts);
            setSelectedProduct(null); // Clear single selection
            setShowOfferDialog(true);
        } else {
            // Single product offer
            const product = products.find(p => p.id === offer.productId);
            if (product) {
                setSelectedProduct(product);
                setSelectedProducts([]); // Clear group selection
                setShowOfferDialog(true);
            } else if (offer.productId) {
                // Fallback if product not in list (maybe inactive?)
                setSelectedProduct({ id: offer.productId, name: "Unknown Product" });
                setSelectedProducts([]);
                setShowOfferDialog(true);
            }
        }
    };

    const getProductName = (offer) => {
        if (offer.productIds && offer.productIds.length > 0) {
            const productNames = offer.productIds.map(id => {
                const prod = products.find(p => p.id === id);
                return prod ? prod.name : `Unknown (${id})`;
            });
            return (
                <div>
                    <Chip label="Group Offer" size="small" color="secondary" style={{ marginBottom: 5 }} />
                    <br />
                    <Typography variant="body2" style={{ fontSize: '0.8rem', color: '#666' }}>
                        {productNames.join(", ")}
                    </Typography>
                </div>
            );
        }
        const prod = products.find(p => p.id === offer.productId);
        return prod ? prod.name : offer.productId;
    }

    return (
        <div style={{ padding: "16px", paddingTop: "8px", height: "calc(100vh - 60px)", overflow: "hidden", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                <Typography variant="h4" style={{ fontSize: "1.2em", fontWeight: 'bold' }}>Offers Management</Typography>
                <div style={{ display: "flex", alignItems: "center" }}>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleAddOfferClick}
                        style={{ marginRight: "10px" }}
                    >
                        Add Single Offer
                    </Button>
                    <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<GroupIcon />}
                        onClick={handleAddGroupOfferClick}
                    >
                        Add Group Offer
                    </Button>
                    <IconButton
                        color="primary"
                        onClick={fetchProducts}
                        disabled={loading}
                        style={{ marginLeft: "10px" }}
                        title="Refresh Data"
                    >
                        <RefreshIcon />
                    </IconButton>
                </div>
            </div>

            <TableContainer component={Paper} className="product-list">
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell><strong>Product</strong></TableCell>
                            <TableCell><strong>Type</strong></TableCell>
                            <TableCell><strong>Details</strong></TableCell>
                            <TableCell><strong>Override Price</strong></TableCell>
                            <TableCell align="right"><strong>Actions</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    <Typography variant="body1">Loading data...</Typography>
                                </TableCell>
                            </TableRow>
                        ) : offers.length > 0 ? (
                            offers.map((offer) => (
                                <TableRow key={offer.id || offer._id}>
                                    <TableCell>{getProductName(offer)}</TableCell>
                                    <TableCell>{offer.type || offer.offer_type}</TableCell>
                                    <TableCell>
                                        <b>{offer.type === "BUY X GET Y FREE" ? `Buy ${offer.X} Get ${offer.Y} Free` :
                                            offer.type === "BUY X GET % OFF" ? `Buy ${offer.X} Get ${offer.Y}% Off` :
                                                offer.type === "Flat % OFF" ? `${offer.Y}% Off` :
                                                    offer.type === "BUY X FOR FLAT Y" ? `Buy ${offer.X} For Flat ${offer.Y}` : ""}</b>
                                    </TableCell>
                                    <TableCell><b>{offer.overridePrice || "-"}</b></TableCell>
                                    <TableCell align="right">
                                        <IconButton color="error" onClick={() => handleDelete(offer.id || offer._id, offer.type || offer.offer_type)}>
                                            <DeleteIcon />
                                        </IconButton>
                                        <IconButton color="primary" onClick={() => sendPrintOffer(offer)}>
                                            <PrintIcon />
                                        </IconButton>
                                        <IconButton color="primary" onClick={() => handleEditClick(offer)}>
                                            <EditIcon />
                                        </IconButton>

                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    No offers found.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Dialog to select product first */}
            <Dialog open={openProductSelect} onClose={() => setOpenProductSelect(false)} fullWidth maxWidth="sm">
                <DialogTitle>Select Product for Offer</DialogTitle>
                <DialogContent style={{ paddingTop: "10px" }}>
                    <Autocomplete
                        options={products}
                        getOptionLabel={(option) => option.name}
                        onChange={handleProductSelect}
                        renderInput={(params) => <TextField {...params} label="Search Product" variant="outlined" />}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenProductSelect(false)}>Cancel</Button>
                </DialogActions>
            </Dialog>

            {/* Dialog to select product group */}
            <Dialog open={openGroupSelect} onClose={() => setOpenGroupSelect(false)} fullWidth maxWidth="sm">
                <DialogTitle>Select Products for Group Offer</DialogTitle>
                <DialogContent style={{ paddingTop: "10px" }}>
                    <Autocomplete
                        multiple
                        options={products}
                        getOptionLabel={(option) => option.name}
                        onChange={(event, newValue) => setSelectedProducts(newValue)}
                        renderInput={(params) => <TextField {...params} label="Search Products" variant="outlined" />}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenGroupSelect(false)}>Cancel</Button>
                    <Button onClick={handleGroupSelect} variant="contained" color="primary">Continue</Button>
                </DialogActions>
            </Dialog>
            {openDeleteDialog ? (
                <DeleteDiaologue
                    open={openDeleteDialog}
                    onClose={handleDeleteClose}
                    onConfirm={handleConfirmDelete}
                    itemName={deleteItemName}
                />
            ) : (
                <div />
            )}
            {/* Reuse ProductOffers component */}
            {showOfferDialog && (selectedProduct || selectedProducts.length > 0) && (
                <ProductOffers
                    product={selectedProduct} // Pass single product if selected
                    groupProducts={selectedProducts} // Pass group products if selected
                    setShowOffers={(val) => {
                        setShowOfferDialog(val);
                        if (!val) fetchOffers(); // Refresh list on close
                    }}
                />
            )}
        </div>
    );
};

export default Offers;
